import * as THREE from 'three'
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import type { SceneManager } from './SceneManager'
import { getThreeObject } from './ObjectManager'
import { registry } from '../components-lib/registry'
import { getScaleDimensionKeys, resolveUniformScaledParams } from '../utils/uniformScaleParams'
import {
  EDITOR_HANDLE_SIZE_MAX,
  editorHandleSphereRadiusFromMaxDim,
} from './editorHandleSizing'

export const HANDLE_LAYER = 1

// 控制柄类型
export type HandleType =
  | 'left' | 'right' | 'front' | 'back'
  | 'topleft' | 'topright' | 'bottomleft' | 'bottomright'
  | 'height'

interface HandleInfo {
  mesh: THREE.Mesh
  type: HandleType
}

type OnResizeCallback = (id: string, updates: Record<string, number>) => void
type OnResizeStartCallback = () => void
type OnResizeEndCallback = () => void

const HANDLE_COLOR_IDLE = 0xffffff
const HANDLE_COLOR_ACTIVE = 0x4499ff
const HEIGHT_HANDLE_COLOR = 0x2266ee
const HEIGHT_HANDLE_RATIO = 1.35

export class ScaleHandles {
  private _sm: SceneManager
  private _group = new THREE.Group()
  private _handles: HandleInfo[] = []
  private _currentId: string | null = null
  private _activeHandle: HandleInfo | null = null

  // 拖拽状态
  private _isDragging = false
  private _dragStart = new THREE.Vector3()
  private _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
  private _verticalPlane = new THREE.Plane()
  private _startParams: Record<string, number> = {}

  // 当前组件的参数键映射（从 scaleMap 解析，null 表示该方向禁用）
  private _wKey: string | null = 'width'
  private _dKey: string | null = 'depth'
  private _hKey: string | null = 'height'

  // 导入模型模式（均匀缩放，不使用 registry）
  private _isImportedModel = false
  // 拖拽开始时的世界空间 BBox 尺寸 + 当前 userScale
  private _importedBaseSize = { w: 1, d: 1, userScale: 1 }

  // 尺寸气泡
  private _sizeBubble: CSS2DObject | null = null
  private _sizeBubbleEl: HTMLDivElement | null = null

  // Raycaster for handles (Layer 1 only)
  private _raycaster = new THREE.Raycaster()

  private _onResizeStart: OnResizeStartCallback
  private _onResize: OnResizeCallback
  private _onResizeEnd: OnResizeEndCallback

  // 双击精确输入回调: (handleType, paramKey, currentVal, label, clientX, clientY)
  onPreciseInput?: (ht: HandleType, paramKey: string, val: number, label: string, cx: number, cy: number) => void

  private _lastClickHandle: HandleInfo | null = null
  private _lastClickTime = 0
  private static readonly DBL_CLICK_MS = 350

  constructor(
    sm: SceneManager,
    onResizeStart: OnResizeStartCallback,
    onResize: OnResizeCallback,
    onResizeEnd: OnResizeEndCallback
  ) {
    this._sm = sm
    this._onResizeStart = onResizeStart
    this._onResize = onResize
    this._onResizeEnd = onResizeEnd

    this._group.name = '__scale_handles__'
    this._group.layers.set(HANDLE_LAYER)
    sm.scene.add(this._group)

    this._createSizeBubble()
    this._bindEvents()
  }

  private _createSizeBubble() {
    const el = document.createElement('div')
    el.style.cssText = `
      background: rgba(20,30,50,0.9);
      color: #4af;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-family: monospace;
      white-space: nowrap;
      pointer-events: none;
      border: 1px solid #4499ff;
      transform: translateX(-50%);
    `
    el.style.display = 'none'
    this._sizeBubbleEl = el
    const obj = new CSS2DObject(el)
    obj.layers.set(0)
    this._sizeBubble = obj
    this._sm.scene.add(obj)
  }

  private _makeHandle(type: HandleType, size: number, isHeight = false): THREE.Mesh {
    let geo: THREE.BufferGeometry
    let mat: THREE.Material

    if (isHeight) {
      // 向上箭头：使用圆柱+圆锥组合
      geo = new THREE.ConeGeometry(size * 0.7, size * HEIGHT_HANDLE_RATIO, 10)
      mat = new THREE.MeshLambertMaterial({ color: HEIGHT_HANDLE_COLOR, depthTest: false })
    } else {
      geo = new THREE.SphereGeometry(size, 10, 10)
      mat = new THREE.MeshLambertMaterial({ color: HANDLE_COLOR_IDLE, depthTest: false })
    }

    const mesh = new THREE.Mesh(geo, mat)
    mesh.layers.set(HANDLE_LAYER)
    mesh.userData.handleType = type
    mesh.renderOrder = 999
    return mesh
  }

  private _buildHandles(bounds: { w: number; d: number; h: number }, position: THREE.Vector3, rotY: number) {
    // 清空旧控制柄
    this._handles.forEach(h => this._group.remove(h.mesh))
    this._handles = []

    const hw = bounds.w / 2
    const hd = bounds.d / 2
    const height = bounds.h
    const maxDim = Math.max(bounds.w, bounds.d, bounds.h, 1)
    const baseHandleSize = editorHandleSphereRadiusFromMaxDim(maxDim)
    const heightHandleSize = Math.min(EDITOR_HANDLE_SIZE_MAX * 1.15, baseHandleSize * 1.1)
    const yOffset = baseHandleSize * 0.9

    const addHandle = (type: HandleType, lx: number, lz: number) => {
      const mesh = this._makeHandle(type, baseHandleSize)
      const localPos = new THREE.Vector3(lx, 0, lz)
      localPos.applyEuler(new THREE.Euler(0, rotY, 0))
      mesh.position.set(position.x + localPos.x, yOffset, position.z + localPos.z)
      this._group.add(mesh)
      this._handles.push({ mesh, type })
    }

    // 左/右控制柄（受 widthParam 控制）
    if (this._wKey !== null) {
      addHandle('left',  -hw, 0)
      addHandle('right',  hw, 0)
    }

    // 前/后控制柄（受 depthParam 控制）
    if (this._dKey !== null) {
      addHandle('front', 0,  hd)
      addHandle('back',  0, -hd)
    }

    // 角点控制柄（仅两个方向都启用时才显示）
    if (this._wKey !== null && this._dKey !== null) {
      addHandle('topleft',     -hw, -hd)
      addHandle('topright',     hw, -hd)
      addHandle('bottomleft',  -hw,  hd)
      addHandle('bottomright',  hw,  hd)
    }

    // 高度控制柄
    if (this._hKey !== null) {
      const heightHandle = this._makeHandle('height', heightHandleSize, true)
      const corner = new THREE.Vector3(hw + baseHandleSize * 2.5, height + baseHandleSize * 3, -hd)
      corner.applyEuler(new THREE.Euler(0, rotY, 0))
      heightHandle.position.set(position.x + corner.x, corner.y, position.z + corner.z)
      this._group.add(heightHandle)
      this._handles.push({ mesh: heightHandle, type: 'height' })
    }
  }

  attach(id: string) {
    this._currentId = id
    this._refreshHandles()
    if (this._sizeBubble) this._sizeBubble.visible = false
  }

  detach() {
    this._currentId = null
    this._handles.forEach(h => this._group.remove(h.mesh))
    this._handles = []
    if (this._sizeBubble) this._sizeBubble.visible = false
    if (this._sizeBubbleEl) this._sizeBubbleEl.style.display = 'none'
  }

  refreshPosition() {
    if (this._currentId) this._refreshHandles()
  }

  private _refreshHandles() {
    const id = this._currentId
    if (!id) return
    const obj3d = getThreeObject(id)
    if (!obj3d) return

    const type = obj3d.userData?.objectType ?? ''
    const sceneObj = (window as any).__sceneStore?.activeObjects?.()?.find((o: any) => o.id === id)
    if (!sceneObj) return

    // ── 导入模型特殊处理：用实际 BBox 构建均匀缩放控制柄 ──
    if (type === 'imported_model' || type === 'dxf_reference') {
      this._isImportedModel = true

      // 如果仍是占位符（模型还在加载），清空控制柄，避免沿用上一选中对象的柄
      if (obj3d.userData.isPlaceholder) {
        this._handles.forEach(h => this._group.remove(h.mesh))
        this._handles = []
        this._sm.markDirty()
        return
      }

      const box = new THREE.Box3().setFromObject(obj3d)
      if (box.isEmpty()) return

      const size = box.getSize(new THREE.Vector3())

      // 只显示角点控制柄（4 个 + height），实现均匀 XZ 缩放
      this._wKey = 'userScale'
      this._dKey = 'userScale'
      this._hKey = null

      const rotY = (sceneObj.rotationY * Math.PI) / 180
      this._buildHandles({ w: size.x, d: size.z, h: size.y }, obj3d.position, rotY)
      this._sm.markDirty()
      return
    }

    // ── 普通组件 ──
    this._isImportedModel = false

    const def = registry.get(type)
    if (!def) return

    const sm = def.scaleMap
    this._wKey = sm?.widthParam  !== undefined ? sm.widthParam  : 'width'
    this._dKey = sm?.depthParam  !== undefined ? sm.depthParam  : 'depth'
    this._hKey = sm?.heightParam !== undefined ? sm.heightParam : 'height'

    const bounds = def.getBounds(resolveUniformScaledParams(def, sceneObj.params))
    const rotY = (sceneObj.rotationY * Math.PI) / 180
    this._buildHandles(bounds, obj3d.position, rotY)
    this._sm.markDirty()
  }

  private _bindEvents() {
    const canvas = this._sm.renderer.domElement
    canvas.addEventListener('pointerdown', this._onPointerDown)
    window.addEventListener('pointermove', this._onPointerMove)
    window.addEventListener('pointerup', this._onPointerUp)
  }

  private _getHandleAtPointer(e: PointerEvent): HandleInfo | null {
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    this._raycaster.setFromCamera(ndc, this._sm.camera)
    this._raycaster.layers.set(HANDLE_LAYER)

    const meshes = this._handles.map(h => h.mesh)
    const hits = this._raycaster.intersectObjects(meshes, false)
    if (!hits.length) return null

    const hitMesh = hits[0].object as THREE.Mesh
    return this._handles.find(h => h.mesh === hitMesh) ?? null
  }

  private _onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 || !this._currentId) return

    const handle = this._getHandleAtPointer(e)
    if (!handle) return

    e.stopPropagation()

    // 双击精确输入
    const now = Date.now()
    const isDoubleClick = this._lastClickHandle === handle && now - this._lastClickTime < ScaleHandles.DBL_CLICK_MS
    this._lastClickHandle = handle
    this._lastClickTime = now

    if (isDoubleClick && this.onPreciseInput && !this._isImportedModel) {
      const id = this._currentId
      const sceneObj = (window as any).__sceneStore?.activeObjects?.()?.find((o: any) => o.id === id)
      if (sceneObj) {
        let paramKey = ''
        let val = 0
        let label = ''
        switch (handle.type) {
          case 'left':
          case 'right':
          case 'topleft':
          case 'topright':
          case 'bottomleft':
          case 'bottomright':
            if (this._wKey) {
              paramKey = this._wKey
              val = sceneObj.params[this._wKey] ?? 10
              label = '长度/宽度'
            }
            break
          case 'front':
          case 'back':
            if (this._dKey) {
              paramKey = this._dKey
              val = sceneObj.params[this._dKey] ?? 10
              label = '进深/宽度'
            }
            break
          case 'height':
            if (this._hKey) {
              paramKey = this._hKey
              val = sceneObj.params[this._hKey] ?? 5
              label = '高度'
            }
            break
        }
        if (paramKey) {
          this.onPreciseInput(handle.type, paramKey, val, label, e.clientX, e.clientY)
          return
        }
      }
    }

    this._activeHandle = handle

    // 高亮
    ;(handle.mesh.material as THREE.MeshLambertMaterial).color.setHex(HANDLE_COLOR_ACTIVE)

    // 记录起点
    const id = this._currentId
    const obj3d = getThreeObject(id!)
    if (!obj3d) return

    const sceneObj = (window as any).__sceneStore?.activeObjects?.()?.find((o: any) => o.id === id)
    if (sceneObj) {
      if (this._isImportedModel && obj3d) {
        // 导入模型：记录世界空间 BBox 尺寸 + 当前 userScale
        const box = new THREE.Box3().setFromObject(obj3d)
        const size = box.getSize(new THREE.Vector3())
        const currentUserScale = (sceneObj.params?.userScale as number) ?? 1
        this._importedBaseSize = { w: size.x, d: size.z, userScale: currentUserScale }
        this._startParams = { w: size.x, d: size.z, h: size.y }
      } else {
        // 使用 scaleMap 解析后的实际参数键读取起始值
        this._startParams = {
          w: this._wKey ? (sceneObj.params[this._wKey] ?? 10) : 10,
          d: this._dKey ? (sceneObj.params[this._dKey] ?? 10) : 10,
          h: this._hKey ? (sceneObj.params[this._hKey] ?? 5)  : 5,
        }
      }
    }

    if (handle.type === 'height') {
      // 高度拖拽用与相机垂直的竖直面
      const camDir = new THREE.Vector3()
      this._sm.camera.getWorldDirection(camDir)
      camDir.y = 0
      camDir.normalize()
      this._verticalPlane.setFromNormalAndCoplanarPoint(camDir, handle.mesh.position)
    } else {
      this._groundPlane.constant = 0
    }

    this._getWorldPos(e, handle.type === 'height', this._dragStart)
    this._sm.controls.enabled = false

    this._onResizeStart()

    // 显示尺寸气泡
    this._showSizeBubble(id!)
  }

  private _onPointerMove = (e: PointerEvent) => {
    if (!this._activeHandle || !this._currentId) return
    if (e.buttons !== 1) return

    this._isDragging = true

    const current = new THREE.Vector3()
    const isHeight = this._activeHandle.type === 'height'
    this._getWorldPos(e, isHeight, current)

    const delta = current.clone().sub(this._dragStart)
    const id = this._currentId
    const sceneObj = (window as any).__sceneStore?.activeObjects?.()?.find((o: any) => o.id === id)
    if (!sceneObj) return

    const def = registry.get(sceneObj.type)
    if (!def && !this._isImportedModel) return

    const MIN_SIZE = 1
    const handle = this._activeHandle

    // 将 delta 转换到对象本地坐标系
    const rotY = (sceneObj.rotationY * Math.PI) / 180
    const localDelta = delta.clone()
    localDelta.applyEuler(new THREE.Euler(0, -rotY, 0))

    // ── 导入模型：均匀缩放（所有控制柄都换算为 userScale）
    if (this._isImportedModel) {
      const { w, d, userScale: baseScale } = this._importedBaseSize
      if (w <= 0 || d <= 0) return

      let newScale = baseScale
      switch (handle.type) {
        case 'left':
          newScale = baseScale * Math.max(0.01, (w - localDelta.x) / w); break
        case 'right':
          newScale = baseScale * Math.max(0.01, (w + localDelta.x) / w); break
        case 'front':
          newScale = baseScale * Math.max(0.01, (d + localDelta.z) / d); break
        case 'back':
          newScale = baseScale * Math.max(0.01, (d - localDelta.z) / d); break
        case 'topleft':
        case 'topright':
        case 'bottomleft':
        case 'bottomright': {
          const wDelta = handle.type.includes('right') ? localDelta.x : -localDelta.x
          const dDelta = handle.type.includes('bottom') ? localDelta.z : -localDelta.z
          const sW = Math.max(0.01, (w + wDelta) / w)
          const sD = Math.max(0.01, (d + dDelta) / d)
          newScale = baseScale * ((sW + sD) / 2)  // 取平均，保持均匀
          break
        }
        default: return
      }
      this._onResize(id, { userScale: Math.round(newScale * 1000) / 1000 })
      this._refreshHandles()
      this._updateSizeBubble(id)
      return
    }

    const roundN = (x: number) => Math.round(x * 10) / 10
    const start = this._startParams
    const wk = this._wKey
    const dk = this._dKey
    const hk = this._hKey

    const dimKeys = def ? getScaleDimensionKeys(def) : []
    const uniformFd =
      !this._isImportedModel && !!def && sceneObj.params?.uniformScale === true && dimKeys.length >= 2

    if (uniformFd) {
      const startByKey: Record<string, number> = {}
      if (wk) startByKey[wk] = start.w
      if (dk) startByKey[dk] = start.d
      if (hk) startByKey[hk] = start.h

      const skipUniform =
        (handle.type === 'height' && (!hk || !dimKeys.includes(hk))) ||
        (['left', 'right'].includes(handle.type) && (!wk || !dimKeys.includes(wk))) ||
        (['front', 'back'].includes(handle.type) && (!dk || !dimKeys.includes(dk))) ||
        (['topleft', 'topright', 'bottomleft', 'bottomright'].includes(handle.type) &&
          (!wk || !dk || !dimKeys.includes(wk) || !dimKeys.includes(dk)))

      if (!skipUniform) {
        const applyR = (r: number): Record<string, number> => {
          const patch: Record<string, number> = {}
          for (const k of dimKeys) {
            const b = startByKey[k]
            if (b === undefined || !Number.isFinite(b)) continue
            patch[k] = roundN(b * r)
          }
          return patch
        }

        let patch: Record<string, number> | null = null
        switch (handle.type) {
          case 'left': {
            const newW = Math.max(MIN_SIZE, start.w - localDelta.x)
            const r = newW / startByKey[wk!]!
            patch = applyR(r)
            break
          }
          case 'right': {
            const newW = Math.max(MIN_SIZE, start.w + localDelta.x)
            const r = newW / startByKey[wk!]!
            patch = applyR(r)
            break
          }
          case 'front': {
            const newD = Math.max(MIN_SIZE, start.d + localDelta.z)
            const r = newD / startByKey[dk!]!
            patch = applyR(r)
            break
          }
          case 'back': {
            const newD = Math.max(MIN_SIZE, start.d - localDelta.z)
            const r = newD / startByKey[dk!]!
            patch = applyR(r)
            break
          }
          case 'topleft':
          case 'topright':
          case 'bottomleft':
          case 'bottomright': {
            const wDelta = handle.type.includes('right') ? localDelta.x : -localDelta.x
            const dDelta = handle.type.includes('bottom') ? localDelta.z : -localDelta.z
            const newW = Math.max(MIN_SIZE, start.w + wDelta)
            const newD = Math.max(MIN_SIZE, start.d + dDelta)
            const rW = newW / start.w
            const rD = newD / start.d
            const r = (rW + rD) / 2
            patch = applyR(r)
            break
          }
          case 'height': {
            const newH = Math.max(MIN_SIZE, start.h + delta.y)
            const r = newH / startByKey[hk!]!
            patch = applyR(r)
            break
          }
          default:
            patch = null
        }
        if (patch && Object.keys(patch).length) {
          this._onResize(id, patch)
          this._refreshHandles()
          this._updateSizeBubble(id)
          return
        }
      }
    }

    let paramKey = ''
    let newValue = 0

    switch (handle.type) {
      case 'left':
        if (!this._wKey) return
        paramKey = this._wKey
        newValue = Math.max(MIN_SIZE, this._startParams.w - localDelta.x)
        break
      case 'right':
        if (!this._wKey) return
        paramKey = this._wKey
        newValue = Math.max(MIN_SIZE, this._startParams.w + localDelta.x)
        break
      case 'front':
        if (!this._dKey) return
        paramKey = this._dKey
        newValue = Math.max(MIN_SIZE, this._startParams.d + localDelta.z)
        break
      case 'back':
        if (!this._dKey) return
        paramKey = this._dKey
        newValue = Math.max(MIN_SIZE, this._startParams.d - localDelta.z)
        break
      case 'topleft':
      case 'topright':
      case 'bottomleft':
      case 'bottomright': {
        if (!this._wKey || !this._dKey) return
        const wDelta = handle.type.includes('right') ? localDelta.x : -localDelta.x
        const dDelta = handle.type.includes('bottom') ? localDelta.z : -localDelta.z
        const newW = Math.max(MIN_SIZE, this._startParams.w + wDelta)
        const newD = Math.max(MIN_SIZE, this._startParams.d + dDelta)
        this._onResize(id, { [this._wKey]: roundN(newW), [this._dKey]: roundN(newD) })
        this._refreshHandles()
        this._updateSizeBubble(id)
        return
      }
      case 'height':
        if (!this._hKey) return
        paramKey = this._hKey
        newValue = Math.max(MIN_SIZE, this._startParams.h + delta.y)
        break
    }

    if (paramKey) {
      this._onResize(id, { [paramKey]: roundN(newValue) })
      this._refreshHandles()
      this._updateSizeBubble(id)
    }
  }

  private _onPointerUp = (_e: PointerEvent) => {
    if (!this._activeHandle) return

    // 恢复控制柄颜色
    const isHeight = this._activeHandle.type === 'height'
    ;(this._activeHandle.mesh.material as THREE.MeshLambertMaterial).color.setHex(
      isHeight ? HEIGHT_HANDLE_COLOR : HANDLE_COLOR_IDLE
    )

    if (this._isDragging) {
      this._onResizeEnd()
    }

    this._activeHandle = null
    this._isDragging = false
    this._sm.controls.enabled = true

    // 隐藏尺寸气泡（延迟消失）
    setTimeout(() => {
      if (!this._activeHandle && this._sizeBubbleEl) {
        this._sizeBubbleEl.style.display = 'none'
      }
    }, 1500)
  }

  private _getWorldPos(e: PointerEvent, vertical: boolean, out: THREE.Vector3): boolean {
    const canvas = this._sm.renderer.domElement
    const rect = canvas.getBoundingClientRect()
    const ndc = new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1
    )
    this._raycaster.setFromCamera(ndc, this._sm.camera)
    this._raycaster.layers.set(0)

    const plane = vertical ? this._verticalPlane : this._groundPlane
    return !!this._raycaster.ray.intersectPlane(plane, out)
  }

  private _showSizeBubble(id: string) {
    if (!this._sizeBubble || !this._sizeBubbleEl) return
    const obj3d = getThreeObject(id)
    if (!obj3d) return

    const box = new THREE.Box3().setFromObject(obj3d)
    const top = new THREE.Vector3(
      (box.min.x + box.max.x) / 2,
      box.max.y + 2,
      (box.min.z + box.max.z) / 2
    )
    this._sizeBubble.position.copy(top)
    this._sizeBubble.visible = true
    this._sizeBubbleEl.style.display = 'block'
    this._updateSizeBubble(id)
  }

  private _updateSizeBubble(id: string) {
    if (!this._sizeBubbleEl) return
    const sceneObj = (window as any).__sceneStore?.activeObjects?.()?.find((o: any) => o.id === id)
    if (!sceneObj) return

    const w = sceneObj.params.width ?? sceneObj.params.w ?? '?'
    const d = sceneObj.params.depth ?? sceneObj.params.d ?? '?'
    const h = sceneObj.params.height ?? sceneObj.params.h ?? '?'
    this._sizeBubbleEl.textContent = `${w}m × ${d}m × ${h}m`

    // 更新气泡位置到对象顶部
    const obj3d = getThreeObject(id)
    if (obj3d && this._sizeBubble) {
      const box = new THREE.Box3().setFromObject(obj3d)
      this._sizeBubble.position.set(
        (box.min.x + box.max.x) / 2,
        box.max.y + 2,
        (box.min.z + box.max.z) / 2
      )
    }
  }

  /** 更新鼠标样式（在主 canvas 的 mousemove 时调用）*/
  updateCursor(e: MouseEvent): void {
    const handle = this._getHandleAtPointer(e as unknown as PointerEvent)
    const canvas = this._sm.renderer.domElement
    if (!handle) {
      canvas.style.cursor = ''
      return
    }
    switch (handle.type) {
      case 'left':
      case 'right':
        canvas.style.cursor = 'ew-resize'; break
      case 'front':
      case 'back':
        canvas.style.cursor = 'ns-resize'; break
      case 'topleft':
      case 'bottomright':
        canvas.style.cursor = 'nesw-resize'; break
      case 'topright':
      case 'bottomleft':
        canvas.style.cursor = 'nwse-resize'; break
      case 'height':
        canvas.style.cursor = 'n-resize'; break
    }
  }

  /** 判断是否有控制柄命中（防止触发对象拖拽） */
  isHandleAt(e: PointerEvent): boolean {
    return this._getHandleAtPointer(e) !== null
  }

  dispose() {
    const canvas = this._sm.renderer.domElement
    canvas.removeEventListener('pointerdown', this._onPointerDown)
    window.removeEventListener('pointermove', this._onPointerMove)
    window.removeEventListener('pointerup', this._onPointerUp)
    this._handles.forEach(h => this._group.remove(h.mesh))
    if (this._sizeBubble) this._sm.scene.remove(this._sizeBubble)
    this._sm.scene.remove(this._group)
  }
}
